import PropTypes from "prop-types";
import { Divider, Grid } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import BaseEnum from "enums/BaseEnum";
import { formatStatusText } from "utils/statusFormatter";

const formatDisplayValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;

const InfoItem = ({ label, value }) => {
  const isCurrency =
    typeof label === "string" && label.toLocaleLowerCase("tr-TR").includes("fiyat");
  const formattedValue =
    formatDisplayValue(value) === "N/A"
      ? "N/A"
      : isCurrency && !`${value}`.includes("₺")
      ? `${value} ₺`
      : value;
  return (
    <Grid item xs={12} md={6}>
      <MDTypography variant="body2" color="text">
        <strong>{label}:</strong> {formattedValue}
      </MDTypography>
    </Grid>
  );
};

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const CommentBlock = ({ label, value }) => (
  <MDBox mb={1.5}>
    <MDTypography variant="button" fontWeight="medium" color="text">
      {label}
    </MDTypography>
    <MDTypography
      variant="body2"
      color="text"
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        whiteSpace: "normal",
      }}
    >
      {formatDisplayValue(value)}
    </MDTypography>
  </MDBox>
);

CommentBlock.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function OrderDetailContent({ order, isCustomer, isResource, isLogistics, isAccountant }) {
  if (!order) return null;

  const request = order.requestFormDTO;
  const customer = request?.customer || order.customer;
  const factoryInfo = request?.fabrikaInfo || order.fabrikaInfo;
  const factory = customer?.fabrikalar?.[0];
  const logisticsForm = order.logisticsFormDTO;
  const displayStatus = isCustomer ? order.customerStage || order.status : order.status;
  const isPaymentPendingStage =
    (order.customerStage || "").toLocaleLowerCase("tr-TR") === "ödeme bekleniyor";
  const showExpectedPaymentDate = isPaymentPendingStage && order.expectedPaymentDate;
  const showManagerComment = !isCustomer;
  const showResourceComment = true;
  const showLogisticsComment = !isCustomer;

  const commentSummary = (
    <MDBox mt={3}>
      <CommentBlock label="Sipariş Yorumu" value={request?.comment} />
      {showManagerComment && (
        <CommentBlock label="Yönetici Yorumu" value={request?.managerComment} />
      )}
    </MDBox>
  );

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("tr-TR");
    } catch (error) {
      return value;
    }
  };

  const formatKilograms = (value, totalTons) => {
    if (typeof totalTons === "number" && !Number.isNaN(totalTons)) {
      const kilograms = totalTons * 1000;
      return `${kilograms.toLocaleString("tr-TR")} kg`;
    }
    if (typeof value === "number" && !Number.isNaN(value)) {
      return `${value.toLocaleString("tr-TR")} kg`;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
    return "N/A";
  };

  if (isAccountant) {
    return (
      <MDBox>
        {commentSummary}
        <MDBox mt={3}>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Sipariş Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Sipariş ID" value={order.orderId || order.id} />
            <InfoItem label="Teslim Tarihi" value={formatDateTime(order.deliveryDate)} />
            <InfoItem label="Durum" value={order.status} />
            <InfoItem label="Talep Edilen Miktar" value={order.amount} />
            <InfoItem label="Ödeme Tipi" value={order.paymentType} />
            <InfoItem label="Vade" value={order.term} />
            <InfoItem label="Toplam Fiyat" value={order.price} />
            <InfoItem label="Ton Başına Fiyat" value={order.pricePerTon} />
            <InfoItem
              label={BaseEnum.RESPONSIBLE_USER}
              value={request?.responsibleUserName || "N/A"}
            />
            <InfoItem label="Müşteri" value={customer?.ticariUnvan || customer?.name} />
          </Grid>
          {showResourceComment && (
            <CommentBlock label="Maden Yorumu" value={request?.resourceComment} />
          )}
        </MDBox>
      </MDBox>
    );
  }

  if (isLogistics) {
    return (
      <MDBox>
        {commentSummary}
        <MDBox mt={3}>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Sipariş Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Talep ID" value={request?.id} />
            <InfoItem
              label="Geliş Tarihi"
              value={formatDateTime(order.deliveryDate || request?.requestedDeliveryDate)}
            />
            <InfoItem label="Miktar" value={request?.amount} />
            <InfoItem
              label="Toplam Kilogram"
              value={formatKilograms(request?.amount, request?.totalTons)}
            />
            <InfoItem label="Fabrika Adı" value={factoryInfo?.fabrikaAdi} />
            <InfoItem label="Fabrika Adresi" value={factoryInfo?.adres} />
            <InfoItem label="Kaynak Durum" value={order.status || request?.status} />
            <InfoItem
              label="Lojistik Durum"
              value={formatStatusText(order.logisticsStatus || request?.logisticsStatus)}
            />
            <InfoItem
              label={BaseEnum.RESPONSIBLE_USER}
              value={request?.responsibleUserName || "N/A"}
            />
          </Grid>
          {showResourceComment && (
            <CommentBlock label="Maden Yorumu" value={request?.resourceComment} />
          )}
        </MDBox>
      </MDBox>
    );
  }

  if (isResource) {
    return (
      <MDBox>
        {commentSummary}
        <MDBox mt={3}>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Müşteri Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Ticari Ünvan" value={customer?.ticariUnvan} />
          </Grid>
        </MDBox>

        <Divider sx={{ my: 3 }} />

        <MDBox>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Sipariş Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Sipariş ID" value={order.id} />
            <InfoItem
              label="Teslim Tarihi"
              value={formatDateTime(request?.requestedDeliveryDate || order.deliveryDate)}
            />
            <InfoItem label="Durum" value={displayStatus} />
            <InfoItem label="Talep Edilen Miktar" value={request?.amount || order.amount} />
            <InfoItem
              label="Sınıflandırma"
              value={request?.classification || order.classification}
            />
            <InfoItem label="Tuz Türü" value={request?.materialType || order.materialType} />
            <InfoItem label="Tuz Boyutu" value={request?.size || order.size} />
            <InfoItem label="Paketleme" value={request?.packaging || order.packaging} />
            <InfoItem
              label={BaseEnum.RESPONSIBLE_USER}
              value={request?.responsibleUserName || "N/A"}
            />
          </Grid>
          {showResourceComment && (
            <CommentBlock label="Maden Yorumu" value={request?.resourceComment} />
          )}
        </MDBox>
      </MDBox>
    );
  }

  return (
    <MDBox>
      {commentSummary}
      {!isCustomer && (
        <>
          <MDBox mt={3}>
            <MDTypography variant="h6" fontWeight="medium" gutterBottom>
              Genel Bilgiler
            </MDTypography>
            <Grid container spacing={2}>
              <InfoItem label={BaseEnum.TRADE_NAME} value={customer?.ticariUnvan} />
              <InfoItem label={BaseEnum.PHONE} value={customer?.telefon} />
              <InfoItem label={BaseEnum.EMAIL} value={customer?.eposta} />
              <InfoItem label={BaseEnum.SECTOR} value={customer?.sector} />
              <InfoItem label={BaseEnum.TAX_OFFICE} value={customer?.vergiDairesi} />
              <InfoItem label={BaseEnum.TAX_NUMBER} value={customer?.vergiNumarasi} />
              <InfoItem label={BaseEnum.ADDRESS} value={customer?.adres} />
            </Grid>
          </MDBox>

          <Divider sx={{ my: 3 }} />

          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" gutterBottom>
              Fabrika Bilgileri
            </MDTypography>
            <Grid container spacing={2}>
              <InfoItem label={BaseEnum.FACTORY_NAME} value={factory?.fabrikaAdi} />
              <InfoItem label={BaseEnum.FACTORY_MANAGER} value={factory?.fabrikaSorumlusu} />
              <InfoItem label={BaseEnum.FACTORY_PHONE} value={factory?.fabrikaTelefon} />
              <InfoItem label={BaseEnum.FACTORY_ADDRESS} value={factory?.adres} />
            </Grid>
          </MDBox>

          <Divider sx={{ my: 3 }} />
        </>
      )}

      <MDBox>
        <MDTypography variant="h6" fontWeight="medium" gutterBottom>
          Sipariş Bilgileri
        </MDTypography>
        <Grid container spacing={2}>
          <InfoItem label={BaseEnum.STATUS} value={displayStatus} />
          {!isCustomer && (
            <>
              <InfoItem
                label={BaseEnum.LOGISTICS_STATUS}
                value={formatStatusText(order.logisticsStatus)}
              />
              <InfoItem label={BaseEnum.SALES_INVOICE_STATUS} value={order.salesInvoiceStatus} />
              <InfoItem
                label={BaseEnum.LOGISTICS_INVOICE_STATUS}
                value={order.logisticsInvoiceStatus}
              />
            </>
          )}
          <InfoItem label={BaseEnum.PRICE} value={request?.price?.toString()} />
          <InfoItem label={BaseEnum.PRICE_PER_TON} value={request?.pricePerTon?.toString()} />
          <InfoItem label={BaseEnum.AMOUNT} value={request?.amount} />
          <InfoItem label={BaseEnum.MATERIAL_TYPE} value={request?.materialType} />
          <InfoItem label={BaseEnum.PAYMENT_TYPE} value={request?.paymentType} />
          <InfoItem label={BaseEnum.TERM} value={request?.term} />
          {showExpectedPaymentDate && (
            <InfoItem
              label={BaseEnum.EXPECTED_PAYMENT_DATE}
              value={formatDateTime(order.expectedPaymentDate)}
            />
          )}
          <InfoItem label={BaseEnum.SIZE} value={request?.size} />
          <InfoItem label={BaseEnum.CLASSIFICATION} value={request?.classification} />
          <InfoItem label={BaseEnum.PACKAGING} value={request?.packaging} />
          <InfoItem
            label={BaseEnum.REQUESTED_DELIVERY_DATE}
            value={formatDateTime(request?.requestedDeliveryDate)}
          />
          <InfoItem
            label={BaseEnum.RESPONSIBLE_USER}
            value={request?.responsibleUserName || "N/A"}
          />
        </Grid>
        {showResourceComment && (
          <CommentBlock label="Maden Yorumu" value={request?.resourceComment} />
        )}
      </MDBox>

      <Divider sx={{ my: 3 }} />
      <MDBox>
        <MDTypography variant="h6" fontWeight="medium" gutterBottom>
          Lojistik
        </MDTypography>
        <Grid container spacing={2}>
          <InfoItem label="Varış Tarihi" value={formatDateTime(logisticsForm?.agreedArrivalTime)} />
          {!isCustomer && (
            <>
              <InfoItem
                label="Lojistik Durumu"
                value={formatStatusText(order.logisticsStatus || request?.logisticsStatus)}
              />
              <InfoItem
                label="Lojistik Fatura Durumu"
                value={order.logisticsInvoiceStatus || "Bilinmiyor"}
              />
            </>
          )}
        </Grid>
        {showLogisticsComment && (
          <CommentBlock label="Lojistik Yorumu" value={request?.logisticsComment} />
        )}
        {logisticsForm?.vehicles?.length > 0 && (
          <MDBox mt={2}>
            {logisticsForm.vehicles.map((vehicle, index) => (
              <MDBox
                key={vehicle.id || index}
                mb={2}
                p={2}
                borderRadius={2}
                border="1px solid"
                borderColor="grey.200"
              >
                <MDTypography variant="button" fontWeight="bold">
                  Araç {index + 1}
                </MDTypography>
                <Grid container spacing={1} mt={1}>
                  <InfoItem label="Dorse Plaka" value={vehicle.dorsePlaka} />
                  <InfoItem label="Çekici Plaka" value={vehicle.cekiciPlaka} />
                  <InfoItem label="Şoför Adı" value={vehicle.soforAdi} />
                  <InfoItem label="Şoför Soyadı" value={vehicle.soforSoyadi} />
                  {!isCustomer ? (
                    <InfoItem label="Şoför Kimlik" value={vehicle.soforKimlik} />
                  ) : null}
                  <InfoItem label="Şoför Telefon" value={vehicle.soforTelefon} />
                  <InfoItem label="Tır Tipi" value={vehicle.tirTipi} />
                </Grid>
              </MDBox>
            ))}
          </MDBox>
        )}
      </MDBox>

      {/* Ek Bilgiler intentionally hidden for all users */}
    </MDBox>
  );
}

OrderDetailContent.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    logisticsStatus: PropTypes.string,
    salesInvoiceStatus: PropTypes.string,
    logisticsInvoiceStatus: PropTypes.string,
    deliveryDate: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentType: PropTypes.string,
    term: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricePerTon: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    classification: PropTypes.string,
    materialType: PropTypes.string,
    size: PropTypes.string,
    packaging: PropTypes.string,
    customer: PropTypes.shape({
      name: PropTypes.string,
      ticariUnvan: PropTypes.string,
    }),
    fabrikaInfo: PropTypes.shape({
      fabrikaAdi: PropTypes.string,
      adres: PropTypes.string,
    }),
    requestFormDTO: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pricePerTon: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amount: PropTypes.string,
      totalTons: PropTypes.number,
      materialType: PropTypes.string,
      paymentType: PropTypes.string,
      term: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      size: PropTypes.string,
      classification: PropTypes.string,
      packaging: PropTypes.string,
      requestedDeliveryDate: PropTypes.string,
      comment: PropTypes.string,
      managerComment: PropTypes.string,
      resourceComment: PropTypes.string,
      status: PropTypes.string,
      createdById: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      logisticsPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      logisticsComment: PropTypes.string,
      logisticsStatus: PropTypes.string,
      responsibleUserName: PropTypes.string,
      fabrikaInfo: PropTypes.shape({
        fabrikaAdi: PropTypes.string,
        adres: PropTypes.string,
      }),
      customer: PropTypes.shape({
        name: PropTypes.string,
        ticariUnvan: PropTypes.string,
        adres: PropTypes.string,
        vergiDairesi: PropTypes.string,
        vergiNumarasi: PropTypes.string,
        eposta: PropTypes.string,
        telefon: PropTypes.string,
        sector: PropTypes.string,
        fabrikalar: PropTypes.arrayOf(
          PropTypes.shape({
            fabrikaAdi: PropTypes.string,
            fabrikaSorumlusu: PropTypes.string,
            fabrikaTelefon: PropTypes.string,
            adres: PropTypes.string,
          })
        ),
      }),
    }),
    logisticsFormDTO: PropTypes.shape({
      agreedArrivalTime: PropTypes.string,
      createdByName: PropTypes.string,
      status: PropTypes.string,
      vehicles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          dorsePlaka: PropTypes.string,
          cekiciPlaka: PropTypes.string,
          soforAdi: PropTypes.string,
          soforSoyadi: PropTypes.string,
          soforKimlik: PropTypes.string,
          soforTelefon: PropTypes.string,
          tirTipi: PropTypes.string,
        })
      ),
    }),
    customerStage: PropTypes.string,
    expectedPaymentDate: PropTypes.string,
  }),
  isCustomer: PropTypes.bool,
  isResource: PropTypes.bool,
  isLogistics: PropTypes.bool,
  isAccountant: PropTypes.bool,
};

OrderDetailContent.defaultProps = {
  isCustomer: false,
  isResource: false,
  isLogistics: false,
  isAccountant: false,
};

export default OrderDetailContent;
